/**
 * Rail — the shared secondary-context-list primitive.
 *
 * The single, standardized "second left column" that sits immediately
 * inline-end of LeftNav. Owns all chrome (width, opaque background,
 * inline-end divider, collapse + persistence, RTL, responsive breakpoint);
 * adopters supply only content. See POLICY_NAVIGATION_RAILS.md and
 * ANALYSIS_LEFT_RAILS.md §6.
 *
 * Collapse can be controlled (pass `collapsed` + `onToggleCollapsed`, e.g.
 * when the adopter also needs the flag to restyle its rows) or
 * uncontrolled (pass `persistKey` and let the Rail persist it via
 * usePersistentCollapse). Controlled wins when `collapsed` is provided.
 *
 * Archetypes (analysis §4) pick a width variant only:
 *   - 'switcher' → lateral navigation between sibling entities (Communities)
 *   - 'list'     → master/detail registries (Resources, Messages)
 */
import React, { useId } from 'react';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { usePersistentCollapse } from '../../hooks/usePersistentCollapse';
import { useIsNarrow } from '../../hooks/useIsNarrow';
import styles from './Rail.module.css';

/** The shared rail breakpoint in px — MUST match --rail-breakpoint in
 *  theme-variables.css (matchMedia can't read the CSS token). Below it the
 *  Rail is FORCE-collapsed: the old CSS-only squeeze shrank the width but
 *  left `collapsed` false, so full-width content rendered crushed into the
 *  56px strip (Skip screenshot 2026-07-09). */
export const RAIL_NARROW_MAX = 960;

export type RailWidthVariant = 'switcher' | 'list';

export interface RailProps {
  /** Required for screen readers — names the Rail's content region. */
  ariaLabel: string;
  /** Column width archetype. Defaults to 'switcher'. */
  widthVariant?: RailWidthVariant;
  /** Header label (already translated) shown beside the collapse toggle. */
  headerLabel?: React.ReactNode;
  /**
   * Opt-in vertical label shown ONLY while collapsed (DEV-1407): a single
   * rotated phrase centered in the narrow strip, so a tucked rail still says
   * what it is ("Actions & Roles", "Qualifications"). Already translated.
   *
   * This is the ONE collapsed-label mechanism — Verification's hand-rolled
   * version (its own `.collapsedLabel` div + a `[data-collapsed]` CSS rule in
   * VerificationPage.module.css) was converged onto this prop. Do not build a
   * second one. It's a ReactNode, not a string, because Verification's label
   * is computed from its current level.
   *
   * Deliberately opt-in: rails that carry their own identity while collapsed
   * (the My Impact rail keeps its signature mini-radar as the top icon) would
   * be cluttered by a label, so the default stays label-less.
   */
  collapsedLabel?: React.ReactNode;
  /** Optional extra header controls (already translated). */
  headerActions?: React.ReactNode;
  /** The list content. */
  children?: React.ReactNode;
  /** Optional footer slot (e.g. a "View all" action). Hidden when collapsed. */
  footer?: React.ReactNode;

  /* ---- Collapse: controlled ---- */
  collapsed?: boolean;
  onToggleCollapsed?: (next: boolean) => void;

  /* ---- Collapse: uncontrolled (used when `collapsed` is undefined) ---- */
  /** localStorage key for persisted collapse (e.g. 'l4e_rail_communities_collapsed'). */
  persistKey?: string;
  /** Initial collapse before anything is stored (Discover-style rails start collapsed). */
  defaultCollapsed?: boolean;

  /** Optional extra class on the <aside>. */
  className?: string;
}

export default function Rail({
  ariaLabel,
  widthVariant = 'switcher',
  headerLabel,
  collapsedLabel,
  headerActions,
  children,
  footer,
  collapsed: collapsedProp,
  onToggleCollapsed,
  persistKey,
  defaultCollapsed = false,
  className,
}: RailProps) {
  const { t } = useAppTranslation('community');
  const bodyId = useId();

  // Internal persistence is only used in uncontrolled mode. The hook is
  // always called (rules of hooks) but ignored when `collapsedProp` is set.
  const internal = usePersistentCollapse(persistKey, defaultCollapsed);
  const isControlled = collapsedProp !== undefined;
  // Narrow viewports force real collapse (content adapts, not just width).
  const isNarrow = useIsNarrow(RAIL_NARROW_MAX);
  const collapsed = isNarrow
    ? true
    : isControlled
      ? !!collapsedProp
      : internal.collapsed;

  const handleToggle = () => {
    const next = !collapsed;
    if (isControlled) {
      onToggleCollapsed?.(next);
    } else {
      internal.toggleCollapsed();
      onToggleCollapsed?.(next);
    }
  };

  const variantClass = widthVariant === 'list' ? styles.list : styles.switcher;

  return (
    <aside
      className={[
        'l4e-rail', // stable global marker for shell-level layout (RTL order)
        styles.rail,
        variantClass,
        collapsed ? styles.collapsed : '',
        className || '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={ariaLabel}
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      <div className={styles.header}>
        {!collapsed && headerLabel != null && (
          <span className={styles.headerLabel}>{headerLabel}</span>
        )}
        {/* Inline-end cluster: optional action(s) + the collapse toggle, which
            always sits at the top inline-end of every rail (the consistent
            placement). */}
        <div className={styles.headerEnd}>
          {!collapsed && headerActions}
          {/* No toggle while force-collapsed — expanding is unavailable
              below the breakpoint, so the button would silently no-op. */}
          {!isNarrow && <button
            type="button"
            className={styles.toggle}
            onClick={handleToggle}
            aria-expanded={!collapsed}
            aria-controls={bodyId}
            title={collapsed ? t('rail.expand', 'Expand list') : t('rail.collapse', 'Collapse list')}
            aria-label={collapsed ? t('rail.expand', 'Expand list') : t('rail.collapse', 'Collapse list')}
          >
            {collapsed ? (
              <PanelLeftOpen size={18} className={styles.toggleIcon} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={18} className={styles.toggleIcon} aria-hidden="true" />
            )}
          </button>}
        </div>
      </div>

      {/* Collapsed vertical label (DEV-1407) — centered over the full strip
          height. pointer-events:none in CSS keeps the expand toggle clickable
          underneath it. */}
      {collapsed && collapsedLabel != null && (
        <div className={styles.collapsedLabel}>
          <span className={styles.collapsedLabelText}>{collapsedLabel}</span>
        </div>
      )}

      <div id={bodyId} className={styles.body}>
        {children}
      </div>

      {!collapsed && footer != null && <div className={styles.footer}>{footer}</div>}
    </aside>
  );
}
